import type { Asset } from "schemas/generated/asset";
import type { Product } from "schemas/generated/product";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useProductList } from "@editor/hooks/useProductList";
import { trpc } from "@editor/utils/trpc";

import { AssetThumbnail } from "@/features/assets/components/AssetCard";
import Description from "@replo/design-system/components/label/Description";
import { Tag } from "@replo/design-system/components/tag/Tag";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import startCase from "lodash-es/startCase";
import { Ban, ImageIcon, ShoppingBag } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ALLOWED_CHAT_ASSET_TYPES } from "replo-utils/lib/assets";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

const INITIAL_ITEMS_TO_LOAD = 10;

function isValidAssetExtension(fileExtension: string): boolean {
  const extension = fileExtension.toLowerCase();
  return ALLOWED_CHAT_ASSET_TYPES.includes(extension);
}

interface ContextItemsMenuProps {
  formRef?: React.RefObject<HTMLFormElement>;
  open: boolean;
  onAssetSelect: (asset: Asset) => void;
  onProductSelect: (product: Product) => void;
  position?: "top" | "bottom";
}

export const ContextItemsMenu = ({
  formRef,
  open,
  onAssetSelect,
  onProductSelect,
  position = "top",
}: ContextItemsMenuProps) => {
  const projectId = useCurrentProjectId();
  const [searchTerm] = useDebouncedState("");
  const [tab, setTab] = React.useState<"assets" | "products">("products");

  const {
    data: assetsData,
    fetchNextPage: fetchNextAssetsPage,
    hasNextPage: hasNextAssetsPage,
  } = trpc.asset.list.useInfiniteQuery(
    projectId
      ? {
          projectId,
          types: ["image"],
          limit: INITIAL_ITEMS_TO_LOAD,
          search: searchTerm,
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const {
    products,
    fetchNextPage: fetchNextProductsPage,
    hasNextPage: hasNextProductsPage,
  } = useProductList({
    projectId,
    limit: INITIAL_ITEMS_TO_LOAD,
  });

  const assets = React.useMemo(
    () => assetsData?.pages?.flatMap((page) => page.assets) ?? [],
    [assetsData],
  );

  const [formHeight, setFormHeight] = React.useState(0);

  // NOTE (Bobby, 2025-06-30): This is to make sure the context menu item is always above the text area even if the text area grows in height
  React.useEffect(() => {
    if (!formRef?.current) {
      return;
    }

    const updateHeight = () => {
      if (formRef.current) {
        setFormHeight(formRef.current.offsetHeight);
      }
    };

    // Initial measurement
    updateHeight();

    // Use ResizeObserver to track changes
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(formRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [formRef]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="absolute z-50 w-full "
      style={
        position === "top"
          ? {
              bottom: `${formHeight + 12}px`, // 12px gap from form
              left: 0,
            }
          : {
              top: `${formHeight + 12}px`, // 12px gap from form
              left: 0,
            }
      }
    >
      <div className="flex flex-col border border-border rounded-lg shadow h-[330px] bg-white">
        <div className="font-medium text-default p-3 border-b border-border text-base">
          Add a reference
        </div>
        <div className="flex flex-row h-full overflow-hidden">
          <div className="flex flex-col gap-1 p-3 border-r border-border">
            <div
              className={twMerge(
                "flex flex-row gap-2 items-center p-1.5 rounded-md transition-colors cursor-pointer hover:bg-menu-item-selected/50 text-sm",
                tab === "products" && "bg-menu-item-selected",
              )}
              onClick={() => setTab("products")}
            >
              <ShoppingBag className="size-3" />
              Products
            </div>
            <div
              className={twMerge(
                "flex flex-row gap-2 items-center p-1.5 rounded-md transition-colors cursor-pointer hover:bg-menu-item-selected/50 text-sm",
                tab === "assets" && "bg-menu-item-selected",
              )}
              onClick={() => setTab("assets")}
            >
              <ImageIcon className="size-3" />
              Assets
            </div>
          </div>
          {tab === "assets" && (
            <AssetContextItemsList
              assets={assets}
              fetchNextAssetsPage={() => void fetchNextAssetsPage()}
              hasNextAssetsPage={hasNextAssetsPage}
              onAssetSelect={onAssetSelect}
            />
          )}
          {tab === "products" && (
            <ProductContextItemsList
              products={products}
              fetchNextProductsPage={() => void fetchNextProductsPage()}
              hasNextProductsPage={hasNextProductsPage}
              onProductSelect={onProductSelect}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const AssetContextItemsList = ({
  assets,
  fetchNextAssetsPage,
  hasNextAssetsPage,
  onAssetSelect,
}: {
  assets: Asset[];
  fetchNextAssetsPage: () => void;
  hasNextAssetsPage: boolean;
  onAssetSelect: (asset: Asset) => void;
}) => {
  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div id="assets-scroll-container" className="flex-1 overflow-y-auto p-2">
        <InfiniteScroll
          dataLength={assets.length}
          next={() => void fetchNextAssetsPage()}
          hasMore={hasNextAssetsPage}
          scrollableTarget="assets-scroll-container"
          loader={<div className="text-center p-2">Loading...</div>}
          style={{ overflow: "visible" }}
        >
          <div className="grid w-full gap-2 pb-3 grid-cols-2 md:grid-cols-3">
            {assets.map((asset) => {
              const isSupported = isValidAssetExtension(asset.fileExtension);

              const assetItem = (
                <div
                  key={asset.id}
                  className={twMerge(
                    "flex flex-col gap-1 p-1.5 rounded-lg transition-colors relative group",
                    isSupported
                      ? "cursor-pointer border border-transparent hover:bg-menu-item-selected/50"
                      : "cursor-not-allowed border border-transparent opacity-60",
                  )}
                  onClick={() => {
                    if (isSupported) {
                      onAssetSelect(asset);
                    }
                  }}
                >
                  <div className="relative">
                    <AssetThumbnail
                      asset={asset}
                      isUploading={false}
                      className={twMerge(
                        "rounded-lg",
                        !isSupported && "grayscale",
                      )}
                    />
                    {!isSupported && (
                      <div className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/40 rounded-lg">
                        <div className="flex flex-col items-center gap-1">
                          <Ban className="h-4 w-4 text-white" />
                          <div className="text-xs text-white font-medium">
                            Not supported
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className={twMerge(
                      "text-sm font-medium",
                      !isSupported && "text-muted",
                    )}
                  >
                    {asset.name}.{asset.fileExtension}
                  </div>
                </div>
              );

              if (!isSupported) {
                return (
                  <Tooltip
                    key={asset.id}
                    content="Only PNG, JPEG, GIF, and WEBP assets are allowed."
                    side="top"
                    triggerAsChild
                  >
                    {assetItem}
                  </Tooltip>
                );
              }

              return assetItem;
            })}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

const ProductContextItemsList = ({
  products,
  fetchNextProductsPage,
  hasNextProductsPage,
  onProductSelect,
}: {
  products: Product[];
  fetchNextProductsPage: () => void;
  hasNextProductsPage: boolean;
  onProductSelect: (product: Product) => void;
}) => {
  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div
        id="products-scroll-container"
        className="flex-1 overflow-y-auto p-2"
      >
        <InfiniteScroll
          dataLength={products.length ?? 0}
          next={() => void fetchNextProductsPage()}
          hasMore={hasNextProductsPage}
          scrollableTarget="products-scroll-container"
          loader={<div className="text-center p-2">Loading...</div>}
          style={{ overflow: "visible" }}
        >
          <div className={twMerge("flex flex-col gap-1")}>
            {products.map((product) => {
              let productImage = product.featuredMedia?.url;
              if (product.media?.[0]?.type === "image") {
                productImage = product.media[0].url;
              }

              const tagVariant: "success" | "info" | "neutral" =
                exhaustiveSwitch(
                  product,
                  "status",
                )({
                  active: "success",
                  draft: "info",
                  archived: "neutral",
                });

              return (
                <div
                  key={product.id}
                  className={twMerge(
                    "flex flex-row gap-1 p-1.5 py-1 items-center rounded-lg transition-colors cursor-pointer",
                    "border border-transparent hover:bg-menu-item-selected/50",
                  )}
                  onClick={() => {
                    onProductSelect(product);
                  }}
                >
                  {productImage ? (
                    <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md overflow-hidden">
                      <img
                        src={productImage}
                        alt={product.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md">
                      <ImageIcon className="text-muted size-4" />
                    </div>
                  )}
                  <div className="flex flex-col w-full pl-1">
                    <div className="text-sm font-medium">{product.title}</div>
                    <div className="flex flex-row gap-1">
                      <Tag size="sm" variant={tagVariant}>
                        {startCase(product.status)}
                      </Tag>
                      <Description
                        size="sm"
                        description={product.slug}
                        layoutClassName="m-0"
                        type="muted"
                        UNSAFE_className="truncate"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};
